'use client';

import { Briefcase, History, Megaphone, Package, PackageSearch, Puzzle, Tag, type LucideIcon } from 'lucide-react';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@ui/components/ui/sidebar';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useUser } from '@/contexts/user-context';
import { useTranslations } from 'next-intl';
import { isPreview } from '@ui/lib/utils';
import NavNotifications from './nav-notifications';
import { useTutorial } from '@/contexts/tutorial-context';

type NavItem = {
  titleKey: string;
  url: string;
  icon?: LucideIcon;
  disabled?: boolean;
};

export const NAV_ITEMS: NavItem[] = [
  {
    titleKey: 'jobs',
    url: '/jobs',
    icon: Briefcase,
  },
  {
    titleKey: 'products',
    url: '/products',
    icon: PackageSearch,
  },
  {
    titleKey: 'history',
    url: '/workflows',
    icon: History,
  },
];

export const ADMIN_NAV_ITEMS: (Omit<NavItem, 'titleKey'> & { title: string })[] = [
  {
    title: 'Tag Management',
    url: '/admin/tags',
    icon: Tag,
  },
  {
    title: 'Product Management',
    url: '/admin/products',
    icon: Puzzle,
  },
  {
    title: 'Price Management',
    url: '/admin/prices',
    icon: Package,
  },
  {
    title: 'Feature Announcements',
    url: '/admin/announcements',
    icon: Megaphone,
  },
];

export function NavMain() {
  const pathname = usePathname();
  const { user } = useUser();
  const { isMobile, setOpenMobile } = useSidebar();
  const { advanceTutorial } = useTutorial();
  const t = useTranslations('Navigation');

  return (
    <>
      <SidebarGroup>
        <SidebarMenu>
          {NAV_ITEMS.map((item) => {
            const title = t(item.titleKey);
            return (
              <SidebarMenuItem key={item.titleKey}>
                {item.disabled ? (
                  <SidebarMenuButton tooltip={title} disabled={item.disabled}>
                    {item.icon && <item.icon />}
                    <span>{title}</span>
                  </SidebarMenuButton>
                ) : (
                  <Link
                    href={item.url}
                    onClick={() => {
                      if (isMobile) setOpenMobile(false);
                    }}
                  >
                    <SidebarMenuButton
                      tooltip={title}
                      isActive={item.url === pathname}
                      {...(item.url === '/products' && {
                        'data-tutorial': 'sidebar-products',
                        onClick: () => advanceTutorial(),
                      })}
                    >
                      {item.icon && <item.icon />}
                      <span>{title}</span>
                    </SidebarMenuButton>
                  </Link>
                )}
              </SidebarMenuItem>
            );
          })}
          <NavNotifications />
        </SidebarMenu>
      </SidebarGroup>
      {user.isAdmin && isPreview && (
        <SidebarGroup>
          <SidebarGroupLabel>{t('admin.title')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {ADMIN_NAV_ITEMS.map((item) => {
                const title = item.title;
                return (
                  <SidebarMenuItem key={item.title}>
                    <Link href={item.url}>
                      <SidebarMenuButton tooltip={title} isActive={item.url === pathname} disabled={item.disabled}>
                        {item.icon && <item.icon />}
                        <span>{title}</span>
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </>
  );
}
